import { useMemo } from 'react';
import useGameStore from '@store/useGameStore';
import useSelectionStore from '@store/useSelectionStore';
import useMapStore from '@store/useMapStore';
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType } from '@utils/enums';
import { getTileSize } from '@utils/constants';
import UnitCard from '@components/ui/game/unit-card/UnitCard';
import HorizontalSlider from '@components/ui/HorizontalSlider/HorizontalSlider';
import './DeployedUnitsTab.css';

/**
 * DeployedUnitsTab - Shows all units sorted by initiative (execution order from backend)
 * Clicking a unit selects it on the map and centers the view
 */
export default function DeployedUnitsTab() {
  const currentPlayer = useGameStore((state) => state.currentPlayer);
  // Use direct selector for reactivity (getCurrentUnits uses get() which is not reactive)
  const units = useGameStore((state) => state.game?.dynamic_objects?.units || []);
  const currentClick = useSelectionStore((state) => state.currentClick);
  const handleClick = useSelectionStore((state) => state.handleClick);
  const centerOnTile = useMapStore((state) => state.centerOnTile);

  // Sort all units by initiative (execution order from backend)
  // FAIL LOUDLY: All deployed units MUST have initiative calculated by backend
  const sortedUnits = useMemo(() => {
    if (!units || units.length === 0) return [];

    // Validate all units have initiative - no fallbacks allowed (CLAUDE.md)
    units.forEach(unit => {
      if (unit.initiative === undefined || unit.initiative === null) {
        throw new Error(`Unit ${unit.id} missing initiative. Backend must calculate initiative for all deployed units.`);
      }
    });

    return [...units].sort((a, b) => a.initiative - b.initiative);
  }, [units]);

  // Get selected unit ID
  const selectedUnitId = currentClick?.type === ClickableType.UNIT
    ? currentClick.data?.id
    : null;

  // Handle unit click - select on map and center view
  const handleUnitClick = (unit) => {
    const clickDTO = new ClickDTO(ClickableType.UNIT, unit, 'DeployedUnitsTab');
    handleClick(clickDTO);

    // Center map on the selected unit
    const tileSize = getTileSize();
    centerOnTile(unit.x, unit.y, tileSize);
  };

  if (sortedUnits.length === 0) {
    return (
      <div className="footer-panel-container footer-panel-content">
        <div className="panel-empty-state">
          <p>No units deployed yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="footer-panel-container footer-panel-content">
      <HorizontalSlider className="card-track">
        {sortedUnits.map((unit, index) => {
          const isSelected = selectedUnitId === unit.id;
          const isOwn = unit.player_id === currentPlayer?.id;
          const teamClass = isOwn ? 'own' : 'enemy';

          return (
            <UnitCard
              key={unit.id}
              unit={unit}
              size="medium"
              isSelected={isSelected}
              teamClass={teamClass}
              initiativeOrder={index + 1}
              showHPRing
              onClick={() => handleUnitClick(unit)}
            />
          );
        })}
      </HorizontalSlider>
    </div>
  );
}
